import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { keyframes } from "@emotion/react";
import { motion } from "framer-motion";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { getInstantOffers } from "../offerSection/offerData";
import { useSelector } from "react-redux";

const SpecialOffersDialog = lazy(() => import("../offerSection/specialOfferDialog"));

// Blinking animation for the Offer icon
const blinkAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const MotionIconButton = motion(IconButton);

function SocialMediaBar({ autoOpenOnLoad = false }) {
  const openedOnMount = useRef(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const joinData = useSelector((state) => state.menus?.joinData || []);

  const hasOfferItems = getInstantOffers(joinData[0] || [], Date.now()).length > 0;

  useEffect(() => {
    const isDialogShown = localStorage.getItem("dialogShown");
    if (hasOfferItems && !openedOnMount.current && (autoOpenOnLoad || !isDialogShown)) {
      openedOnMount.current = true;
      setDialogOpen(true);
      localStorage.setItem("dialogShown", "true");
    }
  }, [hasOfferItems, autoOpenOnLoad]);

  const handleOpenDialog = () => hasOfferItems && setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  const socialLinks = [
    { icon: <FacebookIcon />, href: "https://www.facebook.com/share/14wjThCTSc/?mibextid=wwXIfr", color: "#1877F2", label: "Facebook" },
    { icon: <InstagramIcon />, href: "https://www.instagram.com/bbg.food?igsh=NW84Z3ZyMjZxODM5&utm_source=qr", color: "#E1306C", label: "Instagram" },
    { icon: <YouTubeIcon />, href: "https://youtube.com/@bbgfood?si=IUGE5N8oqYJ_tN1W", color: "#FF0000", label: "YouTube" },
  ];

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          bottom: "50%",
          right: 5,
          zIndex: 100,
          transform: "translateY(50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Top Divider */}
        <Box
          sx={{
            width: 2,
            height: 40,
            backgroundColor: "#000",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.3)",
          }}
        />

        {/* Social Icons */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
          {socialLinks.map((link) => (
            <Tooltip title={link.label} key={link.label} placement="left">
              <MotionIconButton
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.label}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.95 }}
                sx={{
                  backgroundColor: "#fff",
                  color: link.color,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": { backgroundColor: "#f0f0f0" },
                }}
              >
                {link.icon}
              </MotionIconButton>
            </Tooltip>
          ))}

          {/* Offer Icon */}
          {hasOfferItems && (
            <Tooltip title="Special Offers" placement="left">
              <MotionIconButton
                onClick={handleOpenDialog}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  animation: `${blinkAnimation} 1s infinite`,
                  color: "#6B8E23",
                  backgroundColor: "#fff",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": { backgroundColor: "#f0f0f0", transform: "scale(1.2)" },
                }}
              >
                <LocalOfferIcon />
                <Typography variant="caption">Offer</Typography>
              </MotionIconButton>
            </Tooltip>
          )}
        </Box>

        {/* Bottom Divider */}
        <Box
          sx={{
            width: 2,
            height: 40,
            backgroundColor: "#000",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.3)",
          }}
        />
      </Box>

      {/* Offer Dialog */}
      {hasOfferItems && dialogOpen && <Suspense fallback={null}><SpecialOffersDialog open={dialogOpen} onClose={handleCloseDialog} /></Suspense>}
    </>
  );
}

export default SocialMediaBar;
